import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { persister, shouldPersistQuery } from '@/lib/persister';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster, toast } from 'sonner';
import { routeTree } from './routeTree.gen';
import { startUnreadBadgeSync } from '@/lib/badge';
// Inter Variable — self-hosted (no external font requests), the UI typeface.
import '@fontsource-variable/inter';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({ routeTree, context: { queryClient } });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Register the service worker once the app shell has mounted. Only in
// production builds — Vite dev's HMR plus an SW caching the shell is a recipe
// for stale-page bugs.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('SW registration failed:', err);
    });
  });
}

// Keep the PWA home-screen badge in sync with the inbox unread count.
startUnreadBadgeSync();

// --- New-version detection ---------------------------------------------------
// The PWA lives for days without ever navigating, so deploys don't reach it
// until a full close-and-reopen. Poll the shell for a changed entry-script
// hash (nav responses are network-first, /index.html is served max-age=0) and
// offer a one-tap reload instead of the reopen ritual.
if (import.meta.env.PROD) {
  // Strip the cache-busting ?v= our own Reload appends (below) so URLs stay
  // clean after the update lands.
  if (window.location.search.includes('v=')) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('v');
      window.history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
    } catch {
      /* cosmetic only */
    }
  }
  // The hash of the bundle we're actually running, straight from the DOM.
  const runningSrc =
    document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')
      ?.getAttribute('src') ?? null;
  const hashOf = (s: string | null) => s?.match(/\/assets\/index-([\w-]+)\.js/)?.[1] ?? null;
  let baseline = hashOf(runningSrc);
  let prompted = false;
  // Cross-launch prompt memory: at most one toast per served version per 6h.
  // Without it, every fresh app launch on a stale bundle re-prompted — the
  // "keeps popping up" loop on iPhone standalone apps.
  const PROMPT_KEY = 'wharf:update-prompted';
  const promptedRecently = (hash: string): boolean => {
    try {
      const [h, ts] = (localStorage.getItem(PROMPT_KEY) ?? '').split('|');
      return h === hash && Date.now() - Number(ts) < 6 * 3600_000;
    } catch {
      return false;
    }
  };
  const checkForUpdate = async () => {
    if (prompted) return;
    try {
      // Unique query so no cache layer (HTTP or a service-worker Cache
      // entry keyed on the bare path) can answer with a stale shell.
      const r = await fetch(`/index.html?upd=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) return;
      const served = hashOf(await r.text());
      if (!served) return;
      if (baseline === null) {
        baseline = served; // couldn't read the DOM script — first poll seeds it
        return;
      }
      if (served !== baseline && !promptedRecently(served)) {
        prompted = true;
        try {
          localStorage.setItem(PROMPT_KEY, `${served}|${Date.now()}`);
        } catch {
          /* private mode — per-launch prompt is the worst case */
        }
        toast('A new version of Wharf is ready', {
          duration: Infinity,
          action: {
            label: 'Reload',
            onClick: () => {
              // Cache-busted NAVIGATION, not location.reload(): iOS standalone
              // apps answer a plain reload from the OS document cache, serving
              // the same stale shell straight back — which is how this prompt
              // got stuck in a loop. A fresh URL forces a real network fetch.
              window.location.href = `/?v=${served}`;
            },
          },
        });
      }
    } catch {
      /* offline — next poll will retry */
    }
  };
  setInterval(() => void checkForUpdate(), 5 * 60_000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForUpdate();
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 7 * 24 * 60 * 60 * 1000,
        dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
      }}
    >
      <TooltipProvider delayDuration={300}>
        <RouterProvider router={router} />
        <Toaster
          // Bottom-left: clear of the iPhone Dynamic Island/clock (top toasts
          // rendered underneath it in the PWA) AND of the compose/reply
          // windows that dock bottom-right on desktop. Safe-area offset lives
          // in index.css.
          position="bottom-left"
          richColors
          closeButton
          // Surface toasts above iframe message bodies + dialogs.
          toastOptions={{ className: 'z-[100]' }}
        />
      </TooltipProvider>
    </PersistQueryClientProvider>
  </React.StrictMode>,
);
